import * as Sentry from '@sentry/vue';
import { computed, ref } from '@vue/composition-api';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useJwt } from '@vueuse/integrations/useJwt';
import { JwtPayload as BaseJwtPayload } from 'jwt-decode';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { apollo, resetApollo } from '@/apollo';
import { graphql } from '@/graphql';
import type {
  ImpersonateMutation,
  ImpersonateMutationVariables,
  LoginMutation,
  LoginMutationVariables,
  RefreshMutation,
  RefreshMutationVariables,
  RegisterDemoUserMutation,
  RegisterDemoUserMutationVariables,
  ResourceType,
  Role,
} from '@/graphql/graphql';
import { clearCsrf } from '@/lib/apollo/csrf';
import { AUTH_COOKIE_NAME, GATEWAY_URL } from '@/lib/env';
import { logger as globalLogger } from '@/logger';

const logger = globalLogger.getLogger('AuthStore');

export let refreshPromise: Promise<void> | undefined;

export const useAuthStore = defineStore('auth', () => {
  const cookies = useCookies([AUTH_COOKIE_NAME]);
  const jwtPayloadCookie = computed(() => cookies.get(AUTH_COOKIE_NAME));
  const { header: jwt } = useJwt<
    object,
    BaseJwtPayload & {
      role: Role;
      resource: { resource: ResourceType; resourceId: string };
    }
  >(jwtPayloadCookie);
  const isAuthenticated = computed(() => !!jwt.value);
  const role = computed(() => jwt.value?.role);

  // Flag to indicate auth state is transitioning (login/logout in progress)
  // Watchers should wait for this to become false before executing queries
  const isTransitioning = ref(false);

  if (isAuthenticated.value && !window.location.href.includes('impersonate=')) {
    // Initially when setting up the store, check if we already have a JWT token and if so, refresh the token
    refresh();
  }

  async function registerAsDemoUser(
    keyboardLayout: RegisterDemoUserMutationVariables['keyboardLayout'],
  ) {
    isTransitioning.value = true;

    await apollo.defaultClient.mutate<
      RegisterDemoUserMutation,
      RegisterDemoUserMutationVariables
    >({
      mutation: graphql(`
        mutation RegisterDemoUser($keyboardLayout: KeyboardLayout) {
          registerDemoUser(data: { keyboardLayout: $keyboardLayout }) {
            user {
              id
              typetuin {
                id
              }
            }
          }
        }
      `),
      variables: { keyboardLayout },
      context: { blockCsrf: true, skipQueue: true } as ApolloContext,
    });

    await resetApollo();

    isTransitioning.value = false;
  }

  async function login({ username, password }: LoginMutationVariables) {
    isTransitioning.value = true;

    await apollo.defaultClient.mutate<LoginMutation, LoginMutationVariables>({
      mutation: graphql(`
        mutation Login($username: String!, $password: String!) {
          loginUser(data: { login: $username, password: $password }) {
            user {
              id
            }
          }
        }
      `),
      variables: { username, password },
      context: { blockCsrf: true, skipQueue: true } as ApolloContext,
    });

    await resetApollo();

    isTransitioning.value = false;
  }

  async function refresh() {
    logger.trace('Refreshing');

    refreshPromise ??= (async () => {
      logger.trace('Fetching refresh');

      await apollo.defaultClient.mutate<
        RefreshMutation,
        RefreshMutationVariables
      >({
        mutation: graphql(`
          mutation Refresh($userId: String!) {
            refreshToken(data: { userId: $userId }) {
              user {
                id
              }
            }
          }
        `),
        variables: { userId: jwt.value?.sub || '' },
        context: { blockCsrf: true, skipQueue: true } as ApolloContext,
      });
    })();

    try {
      await refreshPromise;
      logger.trace('Refreshed');
    } catch (error) {
      logger.error('Refresh', { error });
    } finally {
      refreshPromise = undefined;
    }
  }

  async function impersonate(userId: ImpersonateMutationVariables['userId']) {
    isTransitioning.value = true;

    await apollo.defaultClient.mutate<
      ImpersonateMutation,
      ImpersonateMutationVariables
    >({
      mutation: graphql(`
        mutation Impersonate($userId: ID!) {
          impersonateUser(data: { userId: $userId }) {
            user {
              id
            }
          }
        }
      `),
      variables: { userId },
      context: { blockCsrf: true, skipQueue: true } as ApolloContext,
    });

    await resetApollo();
    isTransitioning.value = false;
  }

  async function logout(type: 'mutation' | 'cookie' = 'mutation') {
    isTransitioning.value = true;

    logger.trace('Logging out', { type });

    clearCsrf();
    Sentry.setUser(null);

    switch (type) {
      case 'cookie':
        cookies.remove(AUTH_COOKIE_NAME, {
          domain: window.location.host.replace(/.*?(mijn|beheer)\./, ''),
          secure: true,
        });
        break;
      case 'mutation':
      default:
        // eslint-disable-next-line no-case-declarations
        const response = await fetch(`${GATEWAY_URL}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
        // eslint-disable-next-line no-case-declarations
        const json = await response.json();

        if (json.redirectUrl) {
          window.location.href = json.redirectUrl;

          // Wait for the redirect to happen
          await new Promise((resolve) => setTimeout(resolve, 2_500));
        }
        break;
    }

    await resetApollo();

    isTransitioning.value = false;
  }

  return {
    jwt,
    isAuthenticated,
    isTransitioning,
    role,
    registerAsDemoUser,
    login,
    refresh,
    impersonate,
    logout,
  };
});

if (module.hot) {
  module.hot.accept(acceptHMRUpdate(useAuthStore, module.hot));
}
